'use client';

import React, { useContext, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslations } from 'next-intl';
import {
  TradeInDevice,
  TRADE_IN_DEVICE_NAMES,
  getTradeInCredit,
  UHX_BONUS,
} from 'content/products/tradeIn';
import { CurrencySymbol, useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { Currency } from 'utils/Currency';
import { RingSelectionContext } from 'components/ring-air/RingSelectionContext';
import { RingProSelectionContext } from 'components/ring-pro/RingProSelectionContext';
import { CustomImage } from 'components/shared/custom-image';
import { UhxIcon } from 'assets/icons/UhxIcon';
import styles from './index.module.css';

// --- Step icons ---

const ShoppingCartIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const RefreshIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M18 6L6 18M6 6l12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// --- Constants ---

const ALL_DEVICES = Object.values(TradeInDevice);

const TRADE_IN_APP_NAMES: Record<TradeInDevice, string> = {
  [TradeInDevice.RING_AIR]: 'Ultrahuman',
  [TradeInDevice.OURA_GEN_4]: 'Oura',
  [TradeInDevice.SAMSUNG_GALAXY_RING]: 'Samsung Galaxy Wearable',
  [TradeInDevice.RING_CONN]: 'RingConn',
  [TradeInDevice.OURA_GEN_3]: 'Oura',
};

const TERM_KEYS = [
  'ring.infoModals.tradeIn.terms.1',
  'ring.infoModals.tradeIn.terms.2',
  'ring.infoModals.tradeIn.terms.3',
  'ring.infoModals.tradeIn.terms.4',
  'ring.infoModals.tradeIn.terms.5',
];

// --- Component ---

interface TradeInModalProps {
  onClose: () => void;
  contextSource?: 'ring' | 'ring_pro';
}

export const TradeInModal: React.FC<TradeInModalProps> = ({
  onClose,
  contextSource = 'ring',
}) => {
  const t = useTranslations('ring');
  const ringContext = useContext(RingSelectionContext);
  const ringProContext = useContext(RingProSelectionContext);

  const ctx = contextSource === 'ring_pro' ? ringProContext : ringContext;

  const productTitle =
    contextSource === 'ring_pro' ? 'Ultrahuman Ring PRO' : 'Ultrahuman Ring AIR';
  const upgradeSteps =
    contextSource === 'ring_pro'
      ? t('ring.infoModals.tradeIn.upgradeStepsPro')
      : t('ring.infoModals.tradeIn.upgradeStepsAir');
  const imageAlt = contextSource === 'ring_pro' ? 'Ring PRO' : 'Ring AIR';

  const {
    tradeInDevice: persistedDevice,
    setTradeInDevice,
    tradeInSerialNumber: persistedSerial,
    setTradeInSerialNumber,
    price,
    hasUhx,
    defaultCurrency,
    store,
    isLoading,
  } = ctx;

  // Local state — not persisted until CTA
  const [localDevice, setLocalDevice] = useState<TradeInDevice | null>(
    persistedDevice,
  );
  const [serialInput, setSerialInput] = useState(persistedSerial || '');
  const [isOpen, setIsOpen] = useState(false);
  const formatPriceWithComma = useFormatPrice();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currencySymbol =
    CurrencySymbol[defaultCurrency.toLowerCase() as Currency];

  const uhxBonusForDevice = store ? UHX_BONUS[store] ?? 0 : 0;

  const hasDeviceSelected = !!localDevice;
  const hasSerialNumber = serialInput.trim().length > 0;
  const showClearIcon = localDevice || (persistedDevice && persistedSerial);
  const canSubmit = hasDeviceSelected && hasSerialNumber;

  // Compute credit from local device selection
  const localCredit =
    localDevice && store ? getTradeInCredit(localDevice, store, hasUhx) : 0;
  const afterTradeInPrice = price - localCredit;

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Body scroll lock
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectOption = (device: TradeInDevice) => {
    setLocalDevice(device);
    setSerialInput('');
    setIsOpen(false);
  };

  const handleSerialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSerialInput(e.target.value);
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalDevice(null);
    setSerialInput('');
    setTradeInDevice(null);
    setTradeInSerialNumber('');
  };

  const handleCTA = () => {
    if (!canSubmit || isLoading) return;
    // Persist to context only on CTA
    setTradeInDevice(localDevice);
    setTradeInSerialNumber(serialInput);
    onClose();
  };

  const subtitleContent = hasDeviceSelected ? (
    <>
      {currencySymbol}
      {formatPriceWithComma(afterTradeInPrice)}{' '}
      {t('ring.infoModals.tradeIn.subtitle.afterTradeIn')} ({currencySymbol}
      {formatPriceWithComma(price)}{' '}
      {t('ring.infoModals.tradeIn.subtitle.dueToday')})*
    </>
  ) : (
    <>
      {currencySymbol}
      {formatPriceWithComma(price)}
    </>
  );

  const ctaLabel = t('ring.infoModals.tradeIn.cta');

  const appName = localDevice
    ? TRADE_IN_APP_NAMES[localDevice]
    : t('ring.infoModals.tradeIn.defaultApp');

  const steps = [
    {
      icon: <ShoppingCartIcon />,
      title: t('ring.infoModals.tradeIn.steps.a.title'),
      description: t('ring.infoModals.tradeIn.steps.a.description', {
        app: appName,
      }),
    },
    {
      icon: <CheckIcon />,
      title: t('ring.infoModals.tradeIn.steps.b.title'),
      description: t('ring.infoModals.tradeIn.steps.b.description'),
    },
    {
      icon: <RefreshIcon />,
      title: t('ring.infoModals.tradeIn.steps.c.title'),
      description: t('ring.infoModals.tradeIn.steps.c.description'),
      subnote: t('ring.infoModals.tradeIn.steps.c.subnote'),
    },
  ];

  const modalContent = (
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="trade-in-modal-title"
    >
      <div className={`${styles.modalContainer} u-hide-scrollbar`}>
        {/* Mobile close button */}
        <button
          onClick={onClose}
          className={`${styles.closeButton} ${styles.lgHidden}`}
          aria-label={t('ring.infoModals.tradeIn.closeModal')}
        >
          <CloseIcon />
        </button>

        {/* Desktop sticky header */}
        <div className={styles.header}>
          <button
            onClick={onClose}
            className={`${styles.closeButton} ${styles.smHidden}`}
            aria-label={t('ring.infoModals.tradeIn.closeModal')}
          >
            <CloseIcon />
          </button>
          <div className={styles.modalContentWithPadding}>
            <div className={styles.headerContent}>
              <div className={styles.headerImageWrapper}>
                <CustomImage
                  src="web_v2/illustrations/air-side.png"
                  alt={imageAlt}
                  width={108}
                  height={108}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              </div>
              <div className={styles.headerTextContent}>
                <b className={styles.headerTitle}>{productTitle}</b>
                <div className={styles.headerSubtitle}>{subtitleContent}</div>
              </div>
              <div className={styles.headerButtonWrapper}>
                <button
                  className={styles.ctaButton}
                  disabled={!canSubmit}
                  onClick={handleCTA}
                >
                  {ctaLabel}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Body content */}
        <div
          className={`${styles.modalContentWithPadding} ${styles.contentWrapper}`}
        >
          <div className={styles.bodyContent}>
            {/* Select ring section */}
            <div className={styles.centeredSection}>
              <h3 id="trade-in-modal-title">
                {t('ring.infoModals.tradeIn.selectRing.title')}
              </h3>
              <p>{t('ring.infoModals.tradeIn.selectRing.subtitle')}</p>
              {uhxBonusForDevice ? (
                <div className={styles.uhxBonusBanner}>
                  <UhxIcon
                    style={{
                      width: 12,
                      height: 12,
                      flexShrink: 0,
                      marginTop: 3,
                    }}
                  />
                  <span>
                    {t('ring.infoModals.tradeIn.uhxBonus.a')}
                    {currencySymbol}
                    {formatPriceWithComma(uhxBonusForDevice)}
                    {t('ring.infoModals.tradeIn.uhxBonus.b')}
                  </span>
                </div>
              ) : null}
            </div>

            {/* Dropdown + serial number */}
            <div className={styles.dropdownGroup}>
              <div ref={dropdownRef} className={styles.dropdownWrapper}>
                <div
                  className={styles.dropdownTrigger}
                  data-open={isOpen}
                  data-has-value={!!localDevice}
                  onClick={() => setIsOpen(!isOpen)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsOpen(!isOpen);
                    }
                  }}
                  aria-expanded={isOpen}
                >
                  {localDevice ? (
                    <div className={styles.triggerContent}>
                      <span className={styles.triggerTitle}>
                        {TRADE_IN_DEVICE_NAMES[localDevice]}
                      </span>
                      <span className={styles.triggerValue}>
                        {t('ring.infoModals.tradeIn.tradeInValue')}{' '}
                        {currencySymbol}
                        {formatPriceWithComma(localCredit)}
                      </span>
                    </div>
                  ) : (
                    t('ring.infoModals.tradeIn.selectRingModel')
                  )}
                  {showClearIcon ? (
                    <button
                      onClick={handleClearSelection}
                      aria-label={t('ring.infoModals.tradeIn.clearSelection')}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        style={{ transform: 'none' }}
                      >
                        <path
                          d="M15 5L5 15M5 5l10 10"
                          stroke="black"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M5 12.5L10 7.5L15 12.5"
                        stroke="black"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                {isOpen && (
                  <div className={styles.dropdownMenu}>
                    {ALL_DEVICES.map((device) => {
                      const credit = store
                        ? getTradeInCredit(device, store, hasUhx)
                        : 0;
                      return (
                        <div
                          key={device}
                          className={styles.dropdownOption}
                          onClick={() => handleSelectOption(device)}
                          role="option"
                          aria-selected={device === localDevice}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleSelectOption(device);
                            }
                          }}
                        >
                          <span className={styles.optionTitle}>
                            {TRADE_IN_DEVICE_NAMES[device]}
                          </span>
                          <span className={styles.optionValue}>
                            {t('ring.infoModals.tradeIn.tradeInValue')}{' '}
                            {currencySymbol}
                            {formatPriceWithComma(credit)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {localDevice && (
                <>
                  <input
                    className={styles.serialNumberInput}
                    type="text"
                    placeholder={t('ring.infoModals.tradeIn.serialPlaceholder')}
                    value={serialInput}
                    onChange={handleSerialChange}
                  />
                  <p className={styles.serialNumberTip}>
                    {t('ring.infoModals.tradeIn.serialTip', {
                      app: TRADE_IN_APP_NAMES[localDevice],
                    })}
                  </p>
                </>
              )}
            </div>

            <div className={styles.divider} />

            {/* How it works section */}
            <div className={styles.centeredSection}>
              <h3>{t('ring.infoModals.tradeIn.howItWorks')}</h3>
              <p>{upgradeSteps}</p>
            </div>

            <div className={styles.heroImage}>
              <CustomImage
                src="web_v2/cart-info-modals/trade-in.png"
                alt={imageAlt}
                width={700}
                height={400}
                style={{ width: '100%', height: 'auto' }}
              />
            </div>

            <div className={styles.stepsSection}>
              {steps.map((step, index) => (
                <div key={index} className={styles.step}>
                  <div className={styles.stepIcon}>{step.icon}</div>
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                  {step.subnote && (
                    <p
                      style={{
                        fontSize: '13px',
                        color: 'rgba(0, 0, 0, 0.45)',
                        marginTop: '4px',
                      }}
                    >
                      {step.subnote}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className={styles.divider} />

            {/* Terms */}
            <div className={styles.termsSection}>
              <h4>{t('ring.infoModals.tradeIn.termsTitle')}</h4>
              <ol>
                {TERM_KEYS.map((termKey, i) => (
                  <li key={i}>{t(termKey)}</li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        {/* Mobile footer */}
        <div className={styles.footer}>
          <div className={styles.modalContentWithPadding}>
            <div className={styles.footerRow}>
              <div className={styles.footerDetails}>
                <div className={styles.footerTitle}>{productTitle}</div>
                <div className={styles.footerSubtitle}>{subtitleContent}</div>
              </div>
              <button
                className={styles.ctaButton}
                disabled={!canSubmit}
                onClick={handleCTA}
              >
                {ctaLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? ReactDOM.createPortal(modalContent, document.body)
    : null;
};
